import React, { useEffect, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import './VideoModal.css';

export const VideoModal = ({ isOpen, video, onClose }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [video]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Pause any other playing HTML5 videos on the page
        if (typeof document !== 'undefined') {
            document.querySelectorAll('video').forEach((v) => {
                if (!v.paused) {
                    try {
                        v.pause();
                    } catch (_) {}
                }
            });
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen || !video) return null;

    const isDirect = Boolean(video.isDirectVideo && video.videoSrc);
    const embedUrl = video.youtubeId
        ? `https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
        : video.embedUrl || video.url || '';

    return (
        <div
            className="video-modal-backdrop"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={video.title}
        >
            <div
                className="video-modal-dialog"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="video-modal-close-btn"
                    aria-label="Close modal"
                >
                    <X size={20} />
                </button>

                <div className="video-modal-player-wrap">
                    {hasError ? (
                        <div className="video-modal-error-fallback">
                            <AlertCircle size={28} />
                            <span>Video unavailable</span>
                        </div>
                    ) : isDirect ? (
                        <video
                            key={video._id || video.id}
                            src={video.videoSrc}
                            poster={video.thumbnail || undefined}
                            controls
                            autoPlay
                            playsInline
                            preload="metadata"
                            className="video-modal-iframe"
                            onError={() => setHasError(true)}
                        />
                    ) : (
                        <iframe
                            src={embedUrl}
                            title={video.title}
                            className="video-modal-iframe"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            onError={() => setHasError(true)}
                        />
                    )}
                </div>

                <div className="video-modal-footer">
                    <div className="video-modal-info">
                        {video.tag && <span className="video-modal-tag">{video.tag}</span>}
                        <h3 className="video-modal-title">{video.title}</h3>
                        {video.description && (
                            <p className="video-modal-desc">{video.description}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="video-modal-dismiss-btn"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VideoModal;
